// @/components/layout/CustomerRequestsBadge.jsx

/**
 * CustomerRequestsBadge Component
 * 
 * A notification badge that displays the count of open customer requests requiring attention.
 * Handles complex status logic to determine which requests need action from the user.
 * 
 * Features:
 * - Dynamically updates when requests change without full re-renders
 * - Displays count of open requests with 9+ handling for large numbers
 * - Intelligently determines "needs attention" state based on multiple factors:
 *   - Request status (invited, accepted, declined)
 *   - Explicit needsAttention flag when available
 *   - Comparison of request and submission timestamps
 * - Multiple store subscriptions to ensure immediate badge updates
 * - Efficient store update handling with useCallback
 * - Zero UI when no open requests exist (returns null)
 * - Auto-updates after submissions to reflect current state
 * 
 * @module components/layout/CustomerRequestsBadge
 */

import React, { useEffect, useState, useCallback } from 'react';
import { appStore } from '@/services/app/store';

/**
 * CustomerRequestsBadge component - Shows notification count for open customer requests
 *
 * @param {Object} props - Component props
 * @param {boolean} props.collapsed - If true, shows compact dot indicator instead of count
 * @returns {React.ReactElement|null} - The badge component or null if no open requests
 */
const CustomerRequestsBadge = ({ collapsed = false }) => {
  // Single state for the count
  const [openCount, setOpenCount] = useState(0);

  /**
   * Calculate open requests count
   * Handles timestamps correctly to ensure badge accuracy
   * 
   * @returns {number} The count of open requests needing attention
   * @private
   */
  const calculateOpenCount = useCallback(() => {
    // Get requests from store
    const requests = appStore.state.data.customerRequests?.requests || [];

    // Get global last submit date
    const globalLastSubmitDate =
      appStore.state.data.customerRequests?.lastSubmitDate ||
      appStore.customerRequestsHandler?.globalLastSubmitDate ||
      null;

    // Count open requests - excluding declined and using needsAttention flag when available
    const count = requests.filter(req => {
      const status = (req.Status || '').toLowerCase();

      // If needsAttention is explicitly defined, use that value directly
      if (req.needsAttention !== undefined) {
        return status !== 'declined' && req.needsAttention === true;
      }

      // For invited requests: always count them
      if (status === 'invited') return true;

      // For declined requests: never count them
      if (status === 'declined') return false;

      // For accepted requests: check if there's a reminder after submission
      if (status === 'accepted') {
        const lastRequest = req.LastLinkingRequest || req.LinkingCreated;
        const lastSubmit = req.LastSubmitDate || globalLastSubmitDate;

        // No submission yet, but already accepted = needs attention
        if (!lastSubmit) return true;

        // Ensure dates are properly compared
        if (lastRequest) {
          const requestDate = new Date(lastRequest);
          const submitDate = new Date(lastSubmit);

          // Needs attention if reminder is newer than submission
          return requestDate > submitDate;
        }
      }

      return false;
    }).length;

    return count;
  }, []);

  // Subscribe to store updates 
  useEffect(() => {
    /**
     * Handler that recalculates the badge count
     * @private
     */
    const handleStoreUpdate = () => {
      const newCount = calculateOpenCount();
      setOpenCount(newCount);
    };

    // Calculate initial value
    handleStoreUpdate();

    // Subscribe to multiple relevant changes
    const unsubscribers = [
      appStore.subscribe('customerRequests', handleStoreUpdate),

      // Also listen for submission events to ensure immediate badge updates
      appStore.subscribe('saveStatus', (status) => {
        if (status?.status === 'saved') {
          handleStoreUpdate();
        }
      }),

      // Listen for direct submission events
      appStore.subscribe('submission', () => {
        handleStoreUpdate();
      })
    ];

    // Clean up subscriptions on unmount
    return () => unsubscribers.forEach(unsub => typeof unsub === 'function' && unsub());
  }, [calculateOpenCount]);

  // Don't show badge if count is 0
  if (openCount === 0) return null;

  // Collapsed mode: small dot indicator positioned top-right of parent
  if (collapsed) {
    return (
      <div className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-primary ring-2 ring-zinc-50" />
    );
  }

  // Expanded mode: full badge with count
  return (
    <div className="flex size-5 items-center justify-center rounded-sm bg-primary text-white text-xxs font-bold -mt-0.5">
      {openCount > 9 ? '9+' : openCount}
    </div>
  );
};

export default CustomerRequestsBadge;